import { all, dropTask, timeout } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import { courseBadgeState } from 'tio-employee/utils/tas/applicationStateMap';
import Divider from 'tio-ui/components/divider';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
  setTrackedTasFieldValue,
} from 'tio-common/utils/tuition-assistance/fields';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';
import { inputsForFields } from 'tio-common/utils/data/inputs-for-fields';
import { LinkTo } from '@ember/routing';
import { MANAGE_ROUTE } from 'tio-employee/utils/tas/manage-link';
import { not, or, and } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, HStack, Section, Header } from 'tio-ui/components/layout';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import ActionableList from 'tio-ui/components/actionable-list';
import Alert from 'tio-employee/components/tas/applications/alert';
import TasFaqs from 'tio-common/components/tas/faqs';
import AlertBanner from 'tio-ui/components/alert-banner';
import ApplicationProcess from 'tio-employee/components/tuition-assistance/application/application-process';
import ClaimsFinancialsCalculationsReadonlyCard from 'tio-common/components/tuition-assistance/program-details/claims-financials/calculations-readonly-card';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import CourseSubmitted from 'tio-employee/components/tas/applications/course-submitted';
import DescriptionList from 'tio-ui/components/description-list';
import { divide } from 'tio-ui/math';
import NavTabs from 'tio-ui/components/nav-tabs';
import PaymentDetailsCard from 'tio-common/components/tas/payment-details-card';
import ProgramCustomReadonlyField from 'tio-common/components/tuition-assistance/program-custom-readonly-field';
import ProgramReadonlyField from 'tio-common/components/tuition-assistance/program-readonly-field';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatFieldValue from 'tio-common/helpers/safe-format-field-value';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import ShowCourseDrawer from 'tio-employee/components/tas/show-course-drawer';
import Table from 'tio-ui/components/table';
import TasApplicationsShowController from 'tio-employee/controllers/authenticated/tas/applications/show';
import type { TasCourseExtension as TASCourseModel } from 'tio-data/data/resources/tas-course.ext';
import ErrorMessages from 'tio-ui/components/error-messages';
import { PhArrowUUpRight, PhChatCenteredText, PhTrash } from 'ember-phosphor-icons';
import PartnerMessagingDrawer from 'tio-employee/components/drawers/partner-messaging-drawer';
import type PartnerService from 'tio-employee/services/partner';
import type { TransitionLogSignature } from 'tio-common/types/tuition-assistance';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord, reloadRecord } from 'tio-data/requests/save-record';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import { abandonProgramRequest } from 'tio-data/requests/tas-program-instance';
import {
  abandonCourseRequest,
  buildTasTransitionFieldData,
  requestEvidenceApprovalRequest,
} from 'tio-data/requests/tas-application-actions';
import { buildTasEmployeeActor } from 'tio-data/utils/tas-actor';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import CategorizedUpload from 'tio-common/components/tuition-assistance/forms/categorized-upload';
import {
  hasRequiredDocuments,
  usesCategorizedUpload,
} from 'tio-employee/utils/tas/evidence-attachments';
import UploadAttachments from 'tio-common/components/tuition-assistance/forms/upload-attachments';
import type { Agreement as AgreementModel } from 'tio-data/data/resources/agreement.type';
import {
  PhPencil,
  PhCheckCircle,
  PhCircle,
  PhCaretLeft,
  PhClipboardText,
} from 'ember-phosphor-icons';
import StripeCardDisplay from 'tio-employee/components/tas/course-cash/stripe-card-display';
import formatDateString from 'tio-common/helpers/format-date-string';
import ReceiptBanner from 'tio-employee/components/tas/course-cash/receipt-banner';
import ReceiptUploadModal from 'tio-employee/components/tas/course-cash/receipt-upload-modal';
import {
  fetchCourseCashTransactions,
  refreshCourseCashTransactions,
  uploadCourseCashReceipt,
  removeCourseCashReceipt,
} from 'tio-employee/utils/course-cash/receipt-actions';
import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type DigitalAssetsService from 'tio-common/services/digital-assets';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';

interface RouteSignature {
  Args: {
    model: {
      application: TasApplicationModel;
      dynamicEligibility: boolean;
      programEligibility: [string, string][];
    };
    controller: TasApplicationsShowController;
  };
}

type LockEmployeeStatusBasedOnType =
  | 'COURSE_START_DATE'
  | 'COURSE_END_DATE'
  | 'FULFILLED_DATE'
  | 'ON_COURSES_SUBMISSION'
  | 'ON_SUBMISSION';

const programTypesToIncludeInstanceAssets = new Set(['TAS.ProgramType.2', 'TAS.ProgramType.3']);

export default class AuthenticatedTasApplicationsShowComponent extends Component<RouteSignature> {
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked submitError = null;
  @tracked hasSubmitted = false;
  @tracked isGradeModalOpen = false;
  @tracked courseToEdit: null | TASCourseModel = null;
  @tracked _pendingGrade: string | null = null;
  @tracked isCourseDrawerOpen = false;
  @tracked courseShowing: null | TASCourseModel = null;
  @tracked deleteApplicationOpen = false;
  @tracked gpaBasedAmount = 0;
  @tracked isPartnerMessagingDrawerOpen = false;
  @tracked courseCashTransactions: CourseCashTransaction[] = [];
  @tracked isReceiptModalOpen = false;
  @tracked selectedReceiptTransaction: CourseCashTransaction | null = null;
  @tracked highlightAddResult = false;
  @tracked pendingGradePointAverage = '';

  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare partner: PartnerService;

  courseApprovalStates = [
    'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    'TAS.ApplicationState.COURSES_APPROVED',
    'TAS.ApplicationState.COURSES_NOT_APPROVED',
    'TAS.ApplicationState.COURSES_REJECTED',
  ];

  programTemplate: TASProgramTemplateModel;

  constructor(owner: Owner, args: RouteSignature['Args']) {
    super(owner, args);
    this.programTemplate = args.model.application.tasProgramInstance.tasProgramTemplate;

    this.pendingGradePointAverage = this.currentGradePointAverage;
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(
      Number(this.pendingGradePointAverage || 0)
    );

    // Load Course Cash transactions from approvables endpoint + assistance data
    this.loadCourseCashTransactions();
  }

  /**
   * Query the /tas-approvables endpoint to get approvable records for this
   * application's assistances, then build the transaction list.
   *
   * The endpoint filters by approval type and context_type and is scoped to
   * the current user by the API's permit_for_user policy.
   */
  loadCourseCashTransactions = async () => {
    this.courseCashTransactions = await fetchCourseCashTransactions(
      this.store,
      this.args.model.application
    );
  };

  get typeClassification() {
    return this.programTemplate.typeClassification;
  }

  get hasPcsCards() {
    return (this.args.model.application.pcsCards?.length ?? 0) > 0;
  }

  get hasUnverifiedCourseCashTransactions() {
    return this.courseCashTransactions.some((t) => t.receiptStatus === 'needed');
  }

  get unverifiedTransactions() {
    return this.courseCashTransactions.filter((t) => t.receiptStatus === 'needed');
  }

  get allAttachments() {
    const { application } = this.args.model;
    const courseAssets = application.activeCourses.flatMap((c) => c.tasAssets ?? []);
    const appAssets = application.tasAssets;
    const programAssets = application.tasProgramInstance.tasAssets ?? [];

    return [
      ...courseAssets,
      ...appAssets,
      // only include programAssets for type 2/3 programs
      ...(programTypesToIncludeInstanceAssets.has(this.typeClassification) ? programAssets : []),
    ];
  }

  get shouldShowAttachments() {
    const typesWithCourseAttachments = [
      'TAS.ProgramType.2',
      'TAS.ProgramType.3',
      'TAS.ProgramType.4',
      'TAS.ProgramType.4.2',
      'TAS.ProgramType.4.3',
    ];
    if (!typesWithCourseAttachments.includes(this.typeClassification)) return false;
    return (
      this.programTemplate.attachmentsForCourseApprovalVisible ||
      this.programTemplate.attachmentsForCourseCompletionVisible ||
      this.args.model.application.state === 'TAS.ApplicationState.ATTEND'
    );
  }

  // Categorized upload is a company capability, not a per-browser opt-in: the
  // company's "AI Attachment Summaries & Real-Time Validation" setting
  // (default on — `!== false`, matching the admin surfaces) plus configured
  // document categories on the template decide it (#9889). The beta toggle
  // that used to gate this surface was unreachable for real TA employees, so
  // configured categories silently fell back to the free-form uploader.
  get useCategorizedUpload() {
    return usesCategorizedUpload({
      isAiSummariesEnabled: this.partner.companySettings?.tuitionAssistance?.isAiSummariesEnabled,
      documentEntries: this.programTemplate.documentEntriesFor(this.assetClassificationString),
    });
  }

  // Phase-aware, matching `assetClassificationString`: during course-approval
  // states this page collects COURSE_APPROVAL_DOCUMENTS, afterwards it is the
  // evidence surface and COMPLETION_DOCUMENTS rules apply (the course-approval
  // flag belongs to the /new guided-wizard flow). `isEvidenceRequired` also
  // honors the per-application waiver. Keeping the asterisk on the same flags
  // as the submit gate is the point — they can never disagree again (#9908).
  get isAttachmentRequired() {
    if (!this.shouldShowAttachments) return false;
    if (this.courseApprovalStates.includes(this.args.model.application.state)) {
      return this.programTemplate.requireAttachmentsForCourseApproval;
    }
    return this.args.model.application.isEvidenceRequired;
  }

  get conditionsForSubmit() {
    return this.programTemplate.applicationApproveEvidenceConditions || [];
  }

  get showSubmitForEvidenceReview() {
    return this.args.model.application.state === 'TAS.ApplicationState.ATTEND';
  }

  get setRequestedTotalAmount() {
    return Math.min(this.gpaBasedAmount, this.args.model.application.requestedTotal);
  }

  submitForEvidenceApproval = dropTask(async () => {
    this.submitError = null;
    this.hasSubmitted = true;
    try {
      await this.saveAgreementsForConditions.perform();
      // WarpDrive mutation builder — replaces adapter.requestCourseEvidence
      // (triggerActionAndReload → request-evidence-approval transition + reload).
      const application = this.args.model.application;
      await this.store.request(
        requestEvidenceApprovalRequest(
          application.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole),
          '',
          buildTasTransitionFieldData(application.fields)
        )
      );
      // request-evidence-approval goes through
      // StateTransitionableController#transition → `head 204` (renders NO
      // resource), so the builder does NOT refresh the cache — keep the reload
      // via the helper.
      await reloadRecord(this.store, application);

      const tasType = this.typeClassification === 'TAS.ProgramType.3' ? 'three' : 'four';
      this.router.transitionTo('authenticated.tas.confirmation', {
        queryParams: {
          tasType: tasType,
          applicationId: this.args.model.application.id,
          evidence: 'true',
        },
      });
    } catch (error) {
      console.error('Failed to submit application:', error);
      this.submitError = error;
    }
  });

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => {
      return saveRecord(this.store, agreement);
    });
    const saved = await all(promises);
    return saved;
  });

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  // This gate guards the EVIDENCE submit, so it reads the evidence-step config
  // only: `COMPLETION_DOCUMENTS_STRING.required` (via `isEvidenceRequired`,
  // which also honors the per-application waiver) decides WHETHER documents
  // are required, and the COMPLETION_DOCUMENTS_ATTACHMENT_FIELD entries decide
  // WHICH. The course-approval flag belongs to the /new guided-wizard flow.
  // Rows alone are not a requirement — a template that lists rows with the
  // requirement off stays submittable.
  get hasRequiredAttachments() {
    const { application } = this.args.model;
    const classification = this.assetClassificationString;

    // Delegated to the shared gate (utils/tas/evidence-attachments) rather than
    // inlined here: the manage screen's Submit-Evidence card needs the identical
    // rule, and when it derived its own it regressed to a presence-only check
    // that enabled Submit at "1 / 3 required" (#10426). Behavior is unchanged
    // from the #9908 fix — configured rows are enforced, AI verdicts never are,
    // and pre-rows carryover evidence still can't be stranded.
    return hasRequiredDocuments({
      isRequired: Boolean(application.isEvidenceRequired),
      classification,
      assets: application.tasAssets,
      documentEntries: this.programTemplate.documentEntriesFor(classification),
      isAiSummariesEnabled: this.partner.companySettings?.tuitionAssistance?.isAiSummariesEnabled,
      hasLegacyRequiredAttachments: Boolean(application.hasRequiredEvidenceAttachments),
    });
  }

  get canSubmitApplication() {
    if (this.args.model.application.ignorePolicies) {
      return true;
    }
    const { isCourseGradeRequired, isMissingCourseGrades, state } = this.args.model.application;

    const sharedConditions =
      this.isActiveParticipant &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      !this.inEligible &&
      state === 'TAS.ApplicationState.ATTEND' &&
      this.hasGpaIfRequired; // TAS.TODO: rework when updating validation

    return isCourseGradeRequired ? sharedConditions && !isMissingCourseGrades : sharedConditions;
  }

  get hasGpaIfRequired() {
    // checks if the GPA field is required
    if (!this.showGPAField) {
      return true;
    }

    const hasField = Boolean(
      getSingleValueForTasField('GRADE_POINT_AVERAGE', this.args.model.application.fields)
    );

    return hasField;
  }

  get expenseType() {
    const expenseType = getSingleValueForTasField('EXPENSE_TYPE', this.programTemplate.fields);

    return expenseType ? `${expenseType}` : `Education`;
  }

  get courseBadge() {
    return courseBadgeState(this.args.model.application.state);
  }

  get showInstructions() {
    const typeClassificationsToShow = [
      'TAS.ProgramType.3',
      'TAS.ProgramType.4',
      'TAS.ProgramType.4.3',
    ];
    return (
      typeClassificationsToShow.includes(this.typeClassification) &&
      this.args.model.application.state === 'TAS.ApplicationState.ATTEND'
    );
  }

  get grades() {
    const exclusiveGradeOptions = getAllValuesForTasField(
      'EXCLUSIVE_ACADEMIC_PERFORMANCE_OPTIONS',
      this.programTemplate.fields
    ) as string[];
    if (exclusiveGradeOptions.length > 0) {
      return exclusiveGradeOptions;
    }

    return inputsForFields['COURSE_GRADE'].options.map((option) => option.value);
  }

  get selectedGrade() {
    return (
      this._pendingGrade ??
      (getSingleValueForTasField('COURSE_GRADE', this.courseToEdit!.fields) as string)
    );
  }

  get programCustomFields() {
    const customFields = this.args.model.application.tasProgramInstance.customFields;
    return customFields.filter((field: { province: string }) => field.province === 'PROGRAM');
  }

  get isTypeTwoOrThreeApplication() {
    return (
      this.typeClassification === 'TAS.ProgramType.3' ||
      this.typeClassification === 'TAS.ProgramType.2'
    );
  }

  get showAddResult() {
    return this.args.model.application.state === 'TAS.ApplicationState.ATTEND';
  }

  get showCompletedGrade() {
    const showGradeStates = [
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
    ];
    return showGradeStates.includes(this.args.model.application.state);
  }

  get applicationAwaitingApproval() {
    const awaitingStates = [
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];
    return awaitingStates.includes(this.args.model.application.state);
  }

  get assetClassificationString() {
    return this.courseApprovalStates.includes(this.args.model?.application?.state ?? '')
      ? 'COURSE_APPROVAL_DOCUMENTS'
      : 'COMPLETION_DOCUMENTS';
  }

  get showDependentInfo() {
    const typeClassificationsToCheck = ['TAS.ProgramType.2', 'TAS.ProgramType.3'];
    return (
      this.programTemplate.isDependentProgram &&
      typeClassificationsToCheck.includes(this.typeClassification)
    );
  }

  get lockEmployeeStatusBasedOn() {
    return this.programTemplate.lockEmployeeStatusBasedOn as LockEmployeeStatusBasedOnType;
  }

  get lockStatus() {
    const lockType: Record<LockEmployeeStatusBasedOnType, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    return this.lockEmployeeStatusBasedOn in lockType
      ? lockType[this.lockEmployeeStatusBasedOn]
      : '';
  }

  get startOrEndDate() {
    if (this.lockStatus === 'start') {
      return getSingleValueForTasField('COURSES_BEGIN_DATE', this.args.model.application.fields);
    }
    if (this.lockStatus === 'end') {
      return getSingleValueForTasField('COURSES_END_DATE', this.args.model.application.fields);
    }
    // For 'submission' lock status, use today's date
    return new Date().toLocaleDateString('en-CA');
  }

  calculateEligibility() {
    const { programEligibility } = this.args.model;
    if (!programEligibility.length) {
      return false;
    }

    const dateValue = this.startOrEndDate;
    // If we're checking start/end dates and they're missing, can't validate eligibility
    if (!dateValue || typeof dateValue !== 'string') {
      return false;
    }

    // Use today's date for 'submission' lock status, otherwise use the provided start or end date.
    const enteredDate = new Date(dateValue);

    return programEligibility.some(([start, end]) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      return enteredDate >= startDate && enteredDate <= endDate;
    });
  }

  get inEligible() {
    const { application, dynamicEligibility } = this.args.model;
    const { ignorePolicies, state } = application;

    if (ignorePolicies || !this.lockStatus) return false;

    const completedStates = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (completedStates.includes(state)) return false;
    if (['fulfilled', 'submission'].includes(this.lockStatus)) return false;

    return !(dynamicEligibility && this.calculateEligibility());
  }

  get requestedChanges() {
    const findNotApprovedDetails = (transitions: TransitionLogSignature[]) => {
      // Filter transitions for "NOT_APPROVED" states
      const notApprovedStates = transitions.filter((transition: TransitionLogSignature) =>
        transition.to.includes('NOT_APPROVED')
      );

      // If there are no "NOT_APPROVED" states, return null or an empty result
      if (notApprovedStates.length === 0) {
        return null;
      }

      // Get the last occurrence based on the timestamp
      const lastNotApproved = notApprovedStates.reduce((latest, current) =>
        new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
      );

      // Extract the relevant details
      return {
        rejectedBy: lastNotApproved.reason?.by?.name || 'Unknown',
        note: lastNotApproved.reason?.note || 'No note provided',
      };
    };
    // spread operator
    return { ...findNotApprovedDetails(this.args.model.application.transitionLog) };
  }

  get changesNeeded() {
    const lastTransition = this.args.model.application.transitionLog.slice(-1)[0];
    return lastTransition?.from.includes('NOT_APPROVED') || false;
  }

  get calculateTotalByGPA() {
    return this.programTemplate.calculateTotalByGPA;
  }

  get showGPAField() {
    return (
      this.calculateTotalByGPA &&
      (this.typeClassification === 'TAS.ProgramType.3' ||
        this.typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  get currentGradePointAverage() {
    const value = getSingleValueForTasField(
      'GRADE_POINT_AVERAGE',
      this.args.model.application.fields
    );
    return value === null || value === undefined ? '' : String(value);
  }

  get isPendingGradePointAverageMissing() {
    return this.showGPAField && this.pendingGradePointAverage.trim() === '';
  }

  get requestedTotal() {
    if (this.showGPAField) {
      return this.setRequestedTotalAmount;
    } else {
      return this.args.model.application.requestedTotal;
    }
  }

  get isPaid() {
    return this.args.model.application.isPaid;
  }

  get courseCashUsed() {
    return this.args.model.application.amounts?.course_cash?.used > 0;
  }

  get isActiveParticipant() {
    return (
      this.args.model.application.tasProgramInstance.employee?.tasParticipant?.isActive || false
    );
  }

  get disableUploadAttachments() {
    return ['TAS.ApplicationState.DEFINE_COURSES', 'TAS.ApplicationState.ABANDONED'].includes(
      this.args.model.application.state
    );
  }

  get shouldShowPaymentDetailsCard() {
    return (
      this.args.model.application.shouldDisplayPaymentDetails &&
      this.args.model.application.paymentsHistory.length > 0
    );
  }

  updateGPA = (value: string) => {
    this.pendingGradePointAverage = value;
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(Number(value || 0));
  };

  saveGPA = async () => {
    const gpa = Number(this.pendingGradePointAverage);
    setTrackedTasFieldValue(this.args.model.application, 'GRADE_POINT_AVERAGE', gpa);
    this.gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
    setTrackedTasFieldValue(
      this.args.model.application,
      'FINAL_APPROVED_TOTAL',
      this.setRequestedTotalAmount
    );
    await saveRecord(this.store, this.args.model.application);
  };

  saveGradeResultTask = dropTask(async () => {
    if (this.showGPAField) {
      await this.saveGPA();
    }
    this.closeGradeModal();
  });

  closeApplicationSubmittedModal = () => {
    this.args.controller.applicationSubmitted = false;
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  scrollToAttachments = () => {
    document
      .getElementById('attachments-section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  clearHighlightAddResultTask = dropTask(async () => {
    await timeout(2000);
    this.highlightAddResult = false;
  });

  scrollToCourses = () => {
    document
      .getElementById('courses-section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.highlightAddResult = true;
    this.clearHighlightAddResultTask.perform();
  };

  openGradeModal = (course: TASCourseModel) => {
    this._pendingGrade = null;
    this.pendingGradePointAverage = this.currentGradePointAverage;
    this.isGradeModalOpen = true;
    this.courseToEdit = course;
  };

  clearCourseToEditTask = dropTask(async () => {
    await timeout(350);
    this.courseToEdit = null;
  });

  closeGradeModal = () => {
    this.isGradeModalOpen = false;
    this._pendingGrade = null;
    // Delay clearing courseToEdit until after the reimagined Modal's
    // 300ms close animation completes to avoid rendering undefined
    this.clearCourseToEditTask.perform();
  };

  onSelectionChange = async (key: string | null) => {
    this._pendingGrade = key;
    setTrackedTasFieldValue(this.courseToEdit!, 'COURSE_GRADE', key);
    await saveRecord(this.store, this.courseToEdit!);
    this._pendingGrade = null;
  };

  openCourseDrawer = (course: TASCourseModel) => {
    this.courseShowing = course;
    this.isCourseDrawerOpen = true;
  };

  closeCourseDrawer = () => {
    this.courseShowing = null;
    this.isCourseDrawerOpen = false;
  };

  toggleDeleteApplicationModal = () => {
    this.deleteApplicationOpen = !this.deleteApplicationOpen;
  };

  openPartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = true;
  };

  closePartnerMessagingDrawer = () => {
    this.isPartnerMessagingDrawerOpen = false;
  };

  get showTrashIcon() {
    if (this.isPaid || this.courseCashUsed) {
      return false;
    }
    const statesNotAllowed = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (statesNotAllowed.includes(this.args.model.application.state)) {
      return false;
    }
    if (
      this.typeClassification === 'TAS.ProgramType.4.2' ||
      this.typeClassification === 'TAS.ProgramType.4.3'
    ) {
      return (
        this.args.model.application.tasProgramInstance.state === 'TAS.ProgramInstanceState.PRIMED'
      );
    }

    return true;
  }

  get showChatIcon() {
    const companySetting =
      this.args.model.application?.tasProgramInstance?.tasProgramTemplate?.company
        ?.companySetting ?? {};
    if (companySetting?.tuitionAssistance?.isPartnerMessagingEnabled) {
      return true;
    }
    return false;
  }

  get isCardUsageReviewEnabled() {
    const companySetting =
      this.args.model.application?.tasProgramInstance?.tasProgramTemplate?.company?.companySetting;
    // Default to true (enabled) when not explicitly configured
    return companySetting?.tuitionAssistance?.isCardUsageReviewEnabled !== false;
  }
  // This is just the total number of messages for now.
  get numberOfUnreadMessages() {
    const partnerMessages = this.args.model.application.comments.filter((comment) =>
      comment.tags?.includes('Partner Messaging')
    );
    const messageCount = partnerMessages.length;
    return messageCount > 0 ? messageCount : '';
  }

  // Course Cash Receipt Upload Handlers
  handleUploadReceipt = (transaction: CourseCashTransaction) => {
    this.selectedReceiptTransaction = transaction;
    this.isReceiptModalOpen = true;
  };

  handleCloseReceiptModal = () => {
    this.isReceiptModalOpen = false;
    this.selectedReceiptTransaction = null;
  };

  handleReceiptUpload = async (
    files: DigitalAssetDetailSignature[],
    transaction: CourseCashTransaction
  ) => {
    await uploadCourseCashReceipt(
      this.store,
      this.digitalAssets,
      this.args.model.application,
      files,
      transaction
    );

    await this.reloadCourseCashAssistances();
  };

  handleRemoveReceipt = async (receipt: CourseCashReceipt, transaction: CourseCashTransaction) => {
    await removeCourseCashReceipt(this.store, receipt);

    await this.reloadCourseCashAssistances();

    const updatedTransaction = this.courseCashTransactions.find((t) => t.id === transaction.id);
    if (updatedTransaction) {
      this.selectedReceiptTransaction = updatedTransaction;
    }
  };

  reloadCourseCashAssistances = async () => {
    this.courseCashTransactions = await refreshCourseCashTransactions(
      this.store,
      this.args.model.application
    );
  };

  deleteApplication = dropTask(async () => {
    try {
      if (this.isPaid || this.courseCashUsed) {
        this.toggleDeleteApplicationModal();
        return;
      } else {
        const application = this.args.model.application;
        const actor = buildTasEmployeeActor(
          this.sessionContext.user,
          this.sessionContext.currentRole
        );
        // WarpDrive mutation builder — replaces adapter.abandonCourse
        // (triggerActionAndReload → abandon transition + reload).
        const abandonCourse = async () => {
          await this.store.request(
            abandonCourseRequest(
              application.id as string,
              actor,
              '',
              buildTasTransitionFieldData(application.fields)
            )
          );
          // `abandon` goes through StateTransitionableController#transition →
          // `head 204` (renders NO resource), so the builder does NOT refresh
          // the cache — keep the reload via the helper.
          await reloadRecord(this.store, application);
        };
        if (['TAS.ProgramType.2', 'TAS.ProgramType.3'].includes(this.typeClassification)) {
          await Promise.all([
            abandonCourse(),
            this.store.request(
              abandonProgramRequest(application.tasProgramInstance.id as string, actor)
            ),
          ]);
        } else {
          await abandonCourse();
        }
      }
    } catch (error) {
      console.error('Failed to delete application:', error);
    }
    // TAS.TODO: temporary routing until tas dashboard is complete
    this.router.transitionTo('authenticated.tas.dashboard');
  });

  <template>
    <VStack>
      <Header>
        <div>
          {{! TAS.TODO: temporary routing until tas dashboard is complete }}
          <LinkTo class="tio-row-loose" @route="authenticated.tas.dashboard">
            <PhCaretLeft @size="20" class="inline" />
            {{t "tas.default"}}
          </LinkTo>
        </div>
      </Header>
      <HStack>
        <VStack class="w-full!">
          <div class="tio-row-between w-full mb-4">
            <NavTabs
              class="flex-1"
              @tabs={{array
                (hash
                  label=(t "tas.applications.default")
                  route="authenticated.tas.applications.show"
                  model=@model.application
                )
                (hash
                  label=(t "tas.history.default")
                  route="authenticated.tas.applications.history"
                  model=@model.application
                )
              }}
            />
            <div class="tio-row-tight">
              {{#if this.showChatIcon}}
                <Button
                  @appearance="minimal"
                  {{on "click" this.openPartnerMessagingDrawer}}
                  class="mb-2"
                  data-test-partner-messaging-button
                >
                  <PhChatCenteredText @size="32" />
                  {{this.numberOfUnreadMessages}}
                </Button>
              {{/if}}
              {{#if this.showTrashIcon}}
                <Button
                  @appearance="minimal"
                  @intent="danger"
                  {{on "click" this.toggleDeleteApplicationModal}}
                  class="mb-2 text-danger"
                >
                  <PhTrash @size="32" />
                </Button>
              {{/if}}
            </div>
          </div>
        </VStack>
      </HStack>
      <HStack>
        <VStack class="w-full lg:w-2/3 lg:flex-none md:w-1/2 md:flex-none">
          {{! Course Cash Receipt Upload Banner — only when card usage review is enabled }}
          {{#if this.isCardUsageReviewEnabled}}
            <ReceiptBanner
              @transactions={{this.courseCashTransactions}}
              @onUploadReceipt={{this.handleUploadReceipt}}
            />
          {{/if}}

          {{#if this.hasPcsCards}}
            <StripeCardDisplay
              @application={{@model.application}}
              @transactions={{this.courseCashTransactions}}
              @onUploadReceipt={{if this.isCardUsageReviewEnabled this.handleUploadReceipt}}
              @showReceiptStatus={{this.isCardUsageReviewEnabled}}
            />
          {{/if}}

          <Section>
            <:header>
              {{String @model.application.displayNameOrSemesterCode}}
              <CourseCashTag
                @isDirectPayProgram={{this.programTemplate.isDirectPayProgram}}
                @size="sm"
                class="font-semibold"
              />
            </:header>
            <:actions>
              <LinkTo
                @route={{MANAGE_ROUTE}}
                @model={{@model.application.id}}
                class="tio-inline-row shrink-0 gap-1.5 tio-text-sm-medium tio-fg-secondary hover:tio-fg-info"
                data-test-new-view-link
              >
                {{t "tuition_assistance.applications.manage.header.new_view"}}
                <PhArrowUUpRight @size="16" />
              </LinkTo>
            </:actions>
            <:body>
              {{#if this.inEligible}}
                <AlertBanner
                  @intent="warning"
                  @title={{t
                    "start_date_eligibility.errorHeader"
                    startEndSubmission=this.lockStatus
                  }}
                  data-test-eligibility-error
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{t "start_date_eligibility.errorText"}}
                  </p>
                </AlertBanner>
              {{/if}}
              {{#if this.changesNeeded}}
                <AlertBanner
                  @intent="warning"
                  @title={{t
                    "tas.application.requested_changes"
                    name=this.requestedChanges.rejectedBy
                  }}
                  data-test-requested-changes
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{this.requestedChanges.note}}
                  </p>
                </AlertBanner>
              {{/if}}

              {{#unless this.isActiveParticipant}}
                <AlertBanner
                  @intent="warning"
                  @title={{t "tuition_assistance.inactive_participant_warnings.dashboard_title"}}
                  class="mb-4"
                >
                  <p class="text-sm">
                    {{t "tuition_assistance.inactive_participant_warnings.dashboard_description"}}
                  </p>
                </AlertBanner>
              {{/unless}}
              <Alert @application={{@model.application}} />
              <h2 class="font-medium tio-fg-secondary my-4">
                {{@model.application.tasProgramInstance.programName}}
              </h2>
              {{! APPLICANT TABLE }}
              <Table @isLoading={{false}}>
                <:thead as |options|>
                  <options.tr>
                    <options.th>
                      {{t "tas.applications.applicant"}}
                    </options.th>
                    <options.th>
                      {{t "tas.applications.amount"}}
                    </options.th>
                    <options.th>{{t "tas.applications.submission_date"}}</options.th>
                    <options.th>{{t "tas.applications.status"}}</options.th>
                  </options.tr>
                </:thead>
                <:tbody as |options|>
                  <options.tr>
                    <options.td>{{@model.application.applicant}}</options.td>
                    <options.td>
                      {{safeFormatNumber
                        (divide this.requestedTotal 100)
                        style="currency"
                        currency="USD"
                      }}
                    </options.td>
                    <options.td>
                      {{safeFormatDate @model.application.applicationSubmittedDate}}
                    </options.td>
                    <options.td>
                      {{#if @model.application.inRepayment}}
                        <Chip @intent="warning">
                          {{t "tuition_assistance.application_details.in_repayment"}}
                        </Chip>
                      {{else}}
                        <Chip @intent={{@model.application.badgeIntent}}>
                          {{@model.application.badgeLabel}}
                        </Chip>
                      {{/if}}
                    </options.td>
                  </options.tr>
                </:tbody>
              </Table>
            </:body>
          </Section>
          <Section>
            <:header>{{t "tas.applications.details"}}</:header>
            <:body>
              {{! APPLICATION DETAILS }}
              <DescriptionList class="w-2/3 md:w-full" as |List|>
                <List.Group>
                  <List.Term>{{t "tas.applications.application_name"}}</List.Term>
                  <List.Details>{{@model.application.applicationName}}</List.Details>
                </List.Group>
                {{#if this.showDependentInfo}}
                  <List.Group>
                    <List.Term>{{t "tas.applications.dependent"}}</List.Term>
                    <List.Details
                    >{{@model.application.tasProgramInstance.dependentName}}</List.Details>
                  </List.Group>
                {{/if}}
                <List.Group>
                  <List.Term>{{t "tas.applications.start_date"}}</List.Term>
                  <List.Details>
                    {{formatDateString @model.application.coursesBeginDate}}
                  </List.Details>
                </List.Group>
                <ProgramReadonlyField
                  @name="SEMESTER_CODE"
                  @programTemplate={{this.programTemplate}}
                  @fields={{@model.application.fields}}
                  as |field|
                >
                  <List.Group>
                    <List.Term>{{field.programLabel}}</List.Term>
                    <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                  </List.Group>
                </ProgramReadonlyField>
                {{#if this.isTypeTwoOrThreeApplication}}
                  <ProgramReadonlyField
                    @name="SCHOOL_INSTITUTION_NAME"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="PROGRAM_MAJOR"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="PROGRAM_SPECIALIZATION"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ESTIMATED_PROGRAM_BEGIN"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{formatDateString field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ESTIMATED_PROGRAM_COMPLETION"
                    @programTemplate={{this.programTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{formatDateString field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  <ProgramReadonlyField
                    @name="ENROLLMENT_OBJECTIVE"
                    {{! @glint-expect-error: not sure }}
                    @programTemplate={{@model.application.programInstance.tasProgramTemplate}}
                    @fields={{@model.application.tasProgramInstance.fields}}
                    as |field|
                  >
                    <List.Group>
                      <List.Term>{{field.programLabel}}</List.Term>
                      <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                    </List.Group>
                  </ProgramReadonlyField>
                  {{#each this.programCustomFields as |customField|}}
                    <ProgramCustomReadonlyField @customField={{customField}} as |field|>
                      <List.Group>
                        <List.Term>{{field.programLabel}}</List.Term>
                        <List.Details>{{safeFormatFieldValue field.value}}</List.Details>
                      </List.Group>
                    </ProgramCustomReadonlyField>
                  {{/each}}
                {{/if}}
              </DescriptionList>
            </:body>
          </Section>
          {{! COURSE DETAILS - EXPENSES }}
          <Section id="courses-section">
            <:header>{{this.expenseType}}</:header>
            <:body>
              <ActionableList @striped={{true}} as |l|>
                {{#each @model.application.activeCourses as |course|}}
                  <l.Row class="w-full">
                    <l.Term
                      class="w-96 cursor-pointer tio-fg-info-body underline"
                      {{on "click" (fn this.openCourseDrawer course)}}
                    >
                      {{course.courseName}}
                    </l.Term>
                    <l.Item
                      class="w-96 cursor-pointer"
                      {{on "click" (fn this.openCourseDrawer course)}}
                    >
                      {{#if @model.application.isCreditBased}}
                        {{course.courseCredit}}
                        {{t "common.credits"}}
                      {{else}}
                        {{safeFormatNumber
                          (divide course.courseTotal 100)
                          style="currency"
                          currency="USD"
                        }}
                      {{/if}}
                    </l.Item>
                    {{#if this.applicationAwaitingApproval}}
                      <l.Item
                        class="tio-row-flush ml-0 mr-2 font-semibold tio-fg-info-body cursor-pointer md:ml-auto"
                      >
                        <Chip @intent={{this.courseBadge.intent}}>
                          {{this.courseBadge.label}}
                        </Chip>
                      </l.Item>
                    {{else if this.showAddResult}}
                      <l.Item
                        class="tio-application-result-control
                          {{if
                            this.highlightAddResult
                            'tio-fg-brand ring-2 tio-ring-brand ring-offset-1'
                            'tio-fg-info-body'
                          }}"
                        {{on "click" (fn this.openGradeModal course)}}
                      >
                        {{#if course.courseGrade}}
                          {{course.courseGrade}}
                          <PhPencil @size="24" class="ml-2 tio-fg-secondary" />
                        {{else}}
                          {{t "tas.benefits_summary.add_result_simple"}}
                        {{/if}}
                      </l.Item>
                    {{else if this.showCompletedGrade}}
                      <l.Item
                        class="tio-row-flush ml-0 mr-2 font-semibold tio-fg-info-body md:ml-auto"
                      >
                        {{course.courseGrade}}
                      </l.Item>
                    {{/if}}
                  </l.Row>
                {{/each}}
              </ActionableList>
            </:body>
          </Section>
          {{#if
            (and this.programTemplate.scholarshipsAreVisible @model.application.scholarships.length)
          }}
            <Section>
              <:header>
                {{t "tas.new.scholarships.default"}}
              </:header>
              <:body>
                {{! SCHOLARSHIPS }}
                <ActionableList @striped={{true}} as |l|>
                  {{#each @model.application.scholarships as |scholarship|}}
                    <l.Row class="w-full">
                      <l.Term class="w-96">{{scholarship.scholarshipName}}</l.Term>
                      <l.Item class="w-96">
                        {{safeFormatNumber
                          (divide scholarship.scholarshipAmount 100)
                          style="currency"
                          currency="USD"
                        }}
                      </l.Item>
                    </l.Row>
                  {{/each}}
                </ActionableList>
              </:body>
            </Section>
          {{/if}}

          {{! ATTACHMENTS }}
          {{#if this.shouldShowAttachments}}
            <Section id="attachments-section">
              <:header>
                <div>
                  {{t "common.attachments"}}
                  {{#if this.isAttachmentRequired}}
                    <span class="tio-fg-danger text-xl">*</span>
                  {{/if}}
                </div>
              </:header>
              <:body>
                {{#if this.useCategorizedUpload}}
                  <CategorizedUpload
                    @programTemplate={{this.programTemplate}}
                    @classification={{this.assetClassificationString}}
                    @attachable={{@model.application}}
                    @attachableType="tasApplication"
                    @existingAssets={{@model.application.tasAssets}}
                    @relatedAssets={{this.allAttachments}}
                    @disabled={{this.disableUploadAttachments}}
                  />
                {{else}}
                  <UploadAttachments
                    @programTemplate={{this.programTemplate}}
                    @classification={{this.assetClassificationString}}
                    @attachable={{@model.application}}
                    @attachableType="tasApplication"
                    @useDropzone={{true}}
                    @hideInstructions={{not this.showInstructions}}
                    @disabled={{this.disableUploadAttachments}}
                    @attachmentListItems={{this.allAttachments}}
                    @hideDiscarded={{true}}
                  />
                {{/if}}
                <div class="tio-row-flush w-full">
                </div>
              </:body>
            </Section>
          {{/if}}
        </VStack>
        {{! Evidence Submission and Payment Details }}
        <VStack class="lg:w-1/3! md:w-1/2!">
          {{! Submit For Evidence Review - ASIDE }}
          {{#if this.showSubmitForEvidenceReview}}
            <Section
              class="w-full"
              @headerClass="flex items-center gap-2 tio-text-sm-medium tracking-wide"
            >
              <:header>
                <PhClipboardText @size="32" />
                <p class="text-balance">{{t "tas.application.submit_evidence_for_review"}}</p>
              </:header>
              <:body>
                <section>
                  {{#if @model.application.isEvidenceRequired}}
                    <button
                      type="button"
                      class="tas-summary-action tio-row cursor-pointer group p-0 w-full text-left"
                      aria-label={{t "tas.benefits_summary.attachments"}}
                      {{on "click" this.scrollToAttachments}}
                    >
                      {{! Same predicate as the submit gate — presence-only
                        `isMissingRequiredEvidence` ticked this after one upload
                        while the gate still (correctly) required every
                        configured document category. }}
                      {{#if this.hasRequiredAttachments}}
                        <PhCheckCircle @size="24" @weight="fill" class="tio-fg-brand shrink-0" />
                      {{else}}
                        <PhCircle
                          @size="24"
                          class="tio-fg-faint group-hover:tio-fg-brand shrink-0 transition-colors"
                        />
                      {{/if}}
                      <span
                        class="group-hover:tio-fg-brand group-hover:underline transition-colors"
                      >{{t "tas.benefits_summary.attachments"}}</span>
                    </button>
                  {{/if}}

                  {{#if @model.application.isCourseGradeRequired}}
                    <button
                      type="button"
                      class="tas-summary-action tio-row cursor-pointer group p-0 w-full text-left"
                      aria-label={{t "tas.application.course_grades"}}
                      {{on "click" this.scrollToCourses}}
                    >
                      {{#if @model.application.isMissingCourseGrades}}
                        <PhCircle
                          @size="24"
                          class="tio-fg-faint group-hover:tio-fg-brand shrink-0 transition-colors"
                        />
                      {{else}}
                        <PhCheckCircle @size="24" @weight="fill" class="tio-fg-brand shrink-0" />
                      {{/if}}
                      <span
                        class="group-hover:tio-fg-brand group-hover:underline transition-colors"
                      >{{t "tas.application.course_grades"}}</span>
                    </button>
                  {{/if}}

                  {{#if
                    (or
                      @model.application.isEvidenceRequired @model.application.isCourseGradeRequired
                    )
                  }}
                    <Divider class="mt-4" />
                  {{/if}}

                  {{#if this.conditionsForSubmit.length}}
                    <div class="tio-row mt-4">
                      <AcceptConditions
                        @conditions={{this.conditionsForSubmit}}
                        @onChange={{this.didUpdateAcceptedConditions}}
                      />
                    </div>
                  {{/if}}
                </section>

                <div class="py-4 text-center">
                  <Button
                    @intent="primary"
                    @isDisabled={{not this.canSubmitApplication}}
                    @isRunning={{this.submitForEvidenceApproval.isRunning}}
                    {{on "click" this.submitForEvidenceApproval.perform}}
                    class="my-4"
                  >
                    {{t "tas.benefits_summary.submit_evidence"}}
                  </Button>
                  <p class="tio-text-sm-light">
                    {{t "tas.benefits_summary.helper_text"}}
                  </p>
                  {{#if this.submitError}}
                    <ErrorMessages
                      @showErrors={{this.hasSubmitted}}
                      @error={{this.submitError}}
                      class="text-sm"
                    />
                  {{/if}}
                </div>
              </:body>
            </Section>
          {{/if}}

          <PaymentDetailsCard
            @showDetails={{this.shouldShowPaymentDetailsCard}}
            @requestedTotal={{this.requestedTotal}}
            @payments={{@model.application.paymentsHistory}}
            @isCreditBased={{@model.application.isCreditBased}}
            @totalCredits={{@model.application.totalCredits}}
          />

          <ClaimsFinancialsCalculationsReadonlyCard
            class="grow md:max-w-xl"
            @claimsFinancialsFields={{@model.application.claimsFinancialsCalculatorInput}}
            @showDetails={{@model.application.tasProgramInstance.tasProgramTemplate.calculateTotalByClaimsFinancials}}
          />

          <ApplicationProcess
            @application={{@model.application}}
            @programTemplate={{this.programTemplate}}
          />
          <TasFaqs @template={{this.programTemplate}} />
        </VStack>
      </HStack>
    </VStack>

    <Modal
      @isOpen={{@controller.applicationSubmitted}}
      @onClose={{this.closeApplicationSubmittedModal}}
      @size="2xl"
    >
      <:header>
        <span class="text-midnight font-semibold">{{t
            "tuition_assistance.submitted.application_submitted"
          }}</span>
      </:header>
      <:body>
        <CourseSubmitted @application={{@model.application}} @courseApprovalRequired={{true}} />
        <div class="mb-4 tio-flex-center">
          <Button
            {{on "click" this.closeApplicationSubmittedModal}}
            @appearance="outlined"
            class="w-48"
          >
            {{t "common.close"}}
          </Button>
        </div>
      </:body>
    </Modal>
    <Modal @isOpen={{this.isGradeModalOpen}} @onClose={{this.closeGradeModal}}>
      <:header>
        <h2 class="text-midnight font-semibold">{{t "tas.benefits_summary.add_result"}}</h2>
      </:header>
      <:body>
        {{#if this.courseToEdit}}
          <div class="tio-flex-between mb-6">
            <h2 class="tio-text-lg-semibold tio-fg-secondary">
              {{String (getSingleValueForTasField "COURSE_NAME" this.courseToEdit.fields)}}
            </h2>
            <div class="w-72">
              <Select
                @items={{this.grades}}
                @selected={{this.selectedGrade}}
                @onChange={{this.onSelectionChange}}
              />
            </div>
          </div>
          {{#if this.showGPAField}}
            <div class="tio-row-between flex-wrap gap-y-4">
              <label
                for="grade-point-average"
                class="font-semibold block mb-1 text-balance tio-fg-secondary flex-1 min-w-56"
              >
                {{t "tas.application.grade_point_average"}}
                <span class="tio-fg-danger">*</span>
              </label>
              <Input
                @size="lg"
                type="number"
                min="0"
                max="4.0"
                step="0.01"
                data-test-grade-point-average
                id="grade-point-average"
                @value={{this.pendingGradePointAverage}}
                class="tas-gpa-input w-40! min-w-40!"
                @onChange={{this.updateGPA}}
              />
            </div>
          {{/if}}
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.saveGradeResultTask.isRunning}}
          @onCancel={{this.closeGradeModal}}
          @onSubmit={{this.saveGradeResultTask.perform}}
          @submitDisabled={{and this.showGPAField this.isPendingGradePointAverageMissing}}
        />
      </:footer>
    </Modal>
    <Modal @isOpen={{this.deleteApplicationOpen}} @onClose={{this.toggleDeleteApplicationModal}}>
      <:header>
        {{#unless this.isPaid}}
          {{t "tas.application.delete_modal.title"}}
        {{/unless}}
      </:header>
      <:body>
        {{#if this.isPaid}}
          <p class="font-bold tio-fg-danger mb-4">
            {{t "tas.application.delete_modal.attention"}}
          </p>

          <p class="tio-fg-danger">
            {{t "tas.application.delete_modal.prepaid_warning"}}
          </p>
        {{else}}
          <p class="mb-4">{{t "tas.application.delete_modal.content"}}</p>
        {{/if}}
      </:body>
      <:footer>
        {{#if this.isPaid}}
          <Footer
            @onSubmit={{this.toggleDeleteApplicationModal}}
            @submitText={{t "common.close"}}
          />
        {{else}}
          <Footer
            @isRunning={{this.deleteApplication.isRunning}}
            @onCancel={{this.toggleDeleteApplicationModal}}
            @onSubmit={{this.deleteApplication.perform}}
            @submitText={{t "common.delete"}}
          />
        {{/if}}
      </:footer>
    </Modal>
    {{#if this.isCourseDrawerOpen}}
      <ShowCourseDrawer
        @isOpen={{this.isCourseDrawerOpen}}
        @onClose={{this.closeCourseDrawer}}
        @course={{this.courseShowing}}
        @programTemplate={{this.programTemplate}}
        {{! @glint-ignore: To reuse this for all courses course needs to be set to null on close }}
        @header={{this.courseShowing.courseName}}
      />
    {{/if}}
    {{#if this.isPartnerMessagingDrawerOpen}}
      <PartnerMessagingDrawer
        @isOpen={{this.isPartnerMessagingDrawerOpen}}
        @onClose={{this.closePartnerMessagingDrawer}}
        @application={{@model.application}}
      />
    {{/if}}
    {{! Course Cash Receipt Upload Modal }}
    {{#if this.isCardUsageReviewEnabled}}
      <ReceiptUploadModal
        @isOpen={{this.isReceiptModalOpen}}
        @transaction={{this.selectedReceiptTransaction}}
        @onClose={{this.handleCloseReceiptModal}}
        @onUpload={{this.handleReceiptUpload}}
        @onRemoveReceipt={{this.handleRemoveReceipt}}
      />
    {{/if}}
  </template>
}
